/**
 * المرحلة 1.4 — the visit's papers, on the doctor's own letterhead (F16, M05,
 * M09, M08): the full visit report, a medical certificate and a referral.
 *
 * Each is a self-contained HTML document, printed (or saved as PDF) through
 * the same offscreen frame as the prescription (services/printPrescription.js).
 * The header band is the prescription's own (`renderHeader`, the doctor's
 * template and colours), so the three read as coming from the same clinic.
 * Each ends with the signature block the document asks for: the doctor's
 * name and title, the licence number (or its line left blank), the time it
 * was signed, and the code with its QR that opens «المستند سليم».
 *
 * Pure string builders: every value is escaped, every label comes from the
 * dictionary (`t('visitPrint.*')`), and the page turns with the language, so
 * the papers are bilingual from the day they exist.
 * Tests/suites/frontend/p14-prints.test.mjs reads what they write.
 */

import { escapeHtml, safeImageSrc, renderHeader, isKnownTemplate, DEFAULT_TEMPLATE, doctorTitleName, bodyPaddingFor } from './prescriptionPrint';
import { formatDate, formatDateTime } from './locale';
import { diagnosisLabel } from './encounter';

const DEFAULT_PRIMARY = '#06b6d4';
const DEFAULT_SECONDARY = '#a855f7';

/** The QR the server rendered: an SVG of our own verify address, inlined; anything else is dropped. */
export function safeQr(svg) {
  const text = String(svg || '').trim();
  if (!/^(<\?xml[^>]*>\s*)?<svg[\s>]/i.test(text)) return '';
  if (/<script|on\w+\s*=|javascript:/i.test(text)) return '';
  return text;
}

/** The letterhead's pieces from a doctor as the server sends it (Backend/lib/encounterStore.js letterheadOf). */
function letterhead(doctor = {}, { lang = 'ar' } = {}) {
  const primary = (doctor.printColors && doctor.printColors.primary) || DEFAULT_PRIMARY;
  const secondary = (doctor.printColors && doctor.printColors.secondary) || DEFAULT_SECONDARY;
  const style = isKnownTemplate(doctor.prescriptionStyle) ? doctor.prescriptionStyle : DEFAULT_TEMPLATE;
  const logo = safeImageSrc(doctor.logo);
  const avatar = safeImageSrc(doctor.image);
  const start = lang === 'en' ? 'left' : 'right';
  const name = escapeHtml(lang === 'en' ? doctor.name || '' : doctorTitleName(doctor.name));
  const title = escapeHtml(doctor.title || '');
  const clinic = escapeHtml((doctor.clinics && doctor.clinics[0] && doctor.clinics[0].name) || '');
  const logoBox = logo ? `<img src="${logo}" alt="" style="width:76px;height:76px;object-fit:contain;flex-shrink:0">` : '';
  const avatarBox = avatar
    ? `<div style="width:68px;height:68px;border-radius:50%;overflow:hidden;border:3px solid rgba(255,255,255,.8);flex-shrink:0"><img src="${avatar}" alt="" style="width:100%;height:100%;object-fit:cover"></div>`
    : `<div style="width:68px;height:68px;border-radius:50%;background:rgba(255,255,255,.2);display:flex;align-items:center;justify-content:center;font-size:28px;flex-shrink:0">🩺</div>`;
  const headerText = (onColour, centred = false) => `
    <div style="text-align:${centred ? 'center' : start}">
      <h1 style="font-size:24px;font-weight:900;color:${onColour ? '#ffffff' : '#0f172a'};margin:0">${name}</h1>
      ${title ? `<p style="font-size:13px;color:${onColour ? 'rgba(255,255,255,.85)' : '#475569'};margin:4px 0 0">${title}</p>` : ''}
      ${clinic ? `<p style="font-size:11px;color:${onColour ? 'rgba(255,255,255,.65)' : '#94a3b8'};margin:3px 0 0">${clinic}</p>` : ''}
    </div>`;
  return { primary, secondary, style, header: renderHeader(style, { primary, secondary, avatarBox, logoBox, headerText }), padding: bodyPaddingFor(style) };
}

/** A section heading in the doctor's colour. */
const heading = (text, primary) => `<h3 class="vr-h" style="font-size:14px;color:${primary};border-bottom:1px solid #e2e8f0;padding-bottom:5px;margin:18px 0 8px">${escapeHtml(text)}</h3>`;

/** The patient and the visit, in one bar. */
function patientBar(rows) {
  const cells = rows
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([label, value]) => `<div><span style="color:#64748b">${escapeHtml(label)}: </span><strong>${escapeHtml(value)}</strong></div>`)
    .join('');
  return `<div class="vr-block" style="display:flex;flex-wrap:wrap;gap:8px 28px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:12px 18px;margin-bottom:16px;font-size:13px">${cells}</div>`;
}

/**
 * The signature block: the doctor's name and title, the licence number (its
 * line left blank when the doctor has none on file), when it was signed, and
 * the code with its QR. A draft says so instead of a code.
 */
export function signatureBlock({ signature = {}, verifyCode = null, verifyUrl = null, qrSvg = null, draft = false, t, lang = 'ar', primary = DEFAULT_PRIMARY }) {
  const name = escapeHtml(lang === 'en' ? signature.name || '' : doctorTitleName(signature.name));
  const licence = signature.licenceNumber ? `<span dir="ltr">${escapeHtml(signature.licenceNumber)}</span>` : '<span style="display:inline-block;min-width:140px;border-bottom:1px dotted #94a3b8">&nbsp;</span>';
  const signedAt = signature.signedAt ? escapeHtml(formatDateTime(signature.signedAt, { lang })) : '';
  const qr = safeQr(qrSvg);
  const code = verifyCode
    ? `<div style="margin-top:6px;font-size:12px;color:#334155">${escapeHtml(t('visitPrint.verifyCode'))}: <strong dir="ltr" style="font-family:monospace;letter-spacing:1px">${escapeHtml(verifyCode)}</strong></div>
       ${verifyUrl ? `<div style="font-size:10px;color:#64748b;direction:ltr;word-break:break-all">${escapeHtml(verifyUrl)}</div>` : ''}`
    : '';
  return `
    <div class="vr-block vr-sign" data-part="signature" style="display:flex;justify-content:space-between;align-items:flex-end;gap:18px;margin-top:22px;padding:14px 18px;border:2px solid ${primary};border-radius:12px">
      <div style="font-size:13px;line-height:1.9">
        <div style="font-weight:800;color:${primary}">${escapeHtml(t('visitPrint.signature'))}</div>
        <div>${escapeHtml(t('visitPrint.doctor'))}: <strong>${name}</strong>${signature.title ? ` — ${escapeHtml(signature.title)}` : ''}</div>
        <div>${escapeHtml(t('visitPrint.licence'))}: ${licence}</div>
        ${draft ? `<div style="color:#b45309;font-weight:800">${escapeHtml(t('visitPrint.draft'))}</div>` : `<div>${escapeHtml(t('visitPrint.signedAt'))}: <strong>${signedAt}</strong></div>`}
        ${code}
      </div>
      ${qr ? `<div style="width:116px;height:116px;flex-shrink:0" class="vr-qr">${qr}</div>` : ''}
    </div>`;
}

/** The whole page around a body: the letterhead, a title, the body, a footer line. */
function page({ head, title, body, lang, poweredBy, watermark = '' }) {
  const dir = lang === 'en' ? 'ltr' : 'rtl';
  return `<!DOCTYPE html>
<html dir="${dir}" lang="${lang === 'en' ? 'en' : 'ar'}">
<head>
<meta charset="utf-8">
<title>${escapeHtml(title)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Noto+Kufi+Arabic:wght@400;600;700;900&display=swap" rel="stylesheet">
<style>
  * { box-sizing: border-box; }
  body { margin: 0; font-family: 'Noto Kufi Arabic', Tahoma, Arial, sans-serif; background: white; color: #0f172a; }
  .vr-block, tr { break-inside: avoid; page-break-inside: avoid; }
  .vr-h { break-after: avoid; page-break-after: avoid; }
  .vr-qr svg { width: 100%; height: 100%; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: start; }
  @media print {
    @page { size: A4; margin: 12mm 0 10mm 0; }
    @page :first { margin-top: 0; }
    body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  }
</style>
</head>
<body>
  <div style="position:relative;background:white" dir="${dir}">
    ${watermark}
    ${head.header}
    <div style="position:relative;z-index:1;padding:${head.padding}">
      <h2 style="text-align:center;font-size:20px;font-weight:900;color:${head.primary};margin:6px 0 14px" data-part="title">${escapeHtml(title)}</h2>
      ${body}
      ${poweredBy ? `<p style="text-align:center;font-size:11px;color:#94a3b8;margin:18px 0 0">${escapeHtml(poweredBy)}</p>` : ''}
    </div>
  </div>
</body>
</html>`;
}

const draftMark = (t) =>
  `<div style="position:fixed;top:40%;left:0;right:0;text-align:center;font-size:96px;font-weight:900;color:#f59e0b;opacity:.08;transform:rotate(-24deg);pointer-events:none;z-index:0">${escapeHtml(t('visitPrint.draftMark'))}</div>`;

const ageOf = (dob, now = new Date()) => {
  if (!dob) return null;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return null;
  let years = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) years -= 1;
  return years >= 0 ? years : null;
};

/** A leave's length as a sentence says it: «يوم واحد», «يومان», «3 أيام», «14 يوم»; «1 day», «3 days». */
export function daysText(n, lang = 'ar') {
  const d = Number(n) || 0;
  if (lang === 'en') return d === 1 ? '1 day' : `${d} days`;
  if (d === 1) return 'يوم واحد';
  if (d === 2) return 'يومان';
  if (d >= 3 && d <= 10) return `${d} أيام`;
  return `${d} يوم`;
}

/** What an amendment changed, in the reader's language (the server stores a code). */
export const AMEND_WHAT = Object.freeze(['soap', 'diagnosis', 'diagnosis_removed', 'procedure', 'procedure_removed', 'vitals', 'vital_removed', 'files']);
export const amendedWhat = (what, t) => (AMEND_WHAT.includes(what) ? t(`visitPrint.what.${what}`) : String(what || ''));

/* ── the visit report ───────────────────────────────────────────────────── */

/**
 * The full visit report (F16): every section of the visit on the letterhead,
 * with the signature block.
 * @param {{ report, t, lang, poweredBy }} args — `report` is GET /api/encounters/:id/report's
 */
export function buildVisitReportHtml({ report, t, lang = 'ar', poweredBy = '' }) {
  if (!report || !report.encounter) throw new Error('buildVisitReportHtml: report is required');
  const e = report.encounter;
  const head = letterhead(report.doctor, { lang });
  const p = head.primary;
  const td = 'padding:6px 10px;border-bottom:1px solid #e5e7eb;font-size:12.5px;vertical-align:top';
  const th = `padding:7px 10px;border-bottom:2px solid ${p};background:#f8fafc;color:${p};font-size:12px`;
  const parts = [];

  const age = ageOf(e.patient && e.patient.dateOfBirth);
  parts.push(
    patientBar([
      [t('visitPrint.patient'), e.patientName || (e.patient && e.patient.name)],
      [t('visitPrint.age'), age !== null ? t('visitPrint.years', { n: age }) : null],
      [t('visitPrint.sex'), e.patient && e.patient.gender ? t(`visitPrint.gender.${e.patient.gender}`) : null],
      [t('visitPrint.visitDate'), formatDate((e.booking && e.booking.date) || e.startedAt, { lang })],
      [t('visitPrint.mode'), t(`visitPrint.modes.${e.consultMode || 'in_person'}`)],
      [t('visitPrint.institution'), report.institution && report.institution.name],
    ]),
  );

  // The diagnosis leads, coded as the institution's policy prints it.
  const withCode = !(e.policy && e.policy.icd10 && e.policy.icd10.onReport === 'text');
  if ((e.diagnoses || []).length) {
    parts.push(heading(t('visitPrint.diagnoses'), p));
    parts.push(
      `<ul class="vr-block" data-part="diagnoses" style="margin:0;padding-inline-start:20px;font-size:13.5px;line-height:1.9">${e.diagnoses
        .map((d) => {
          const label = escapeHtml(diagnosisLabel(d, lang, { withCode }));
          const named = d.primary
            ? `<strong>${label}</strong> <span style="color:#64748b;font-size:11px">(${escapeHtml(t('visitPrint.primary'))})</span>`
            : label;
          const note = d.note ? ` — ${escapeHtml(d.note)}` : '';
          const system = withCode && d.code && d.system ? ` <span style="color:#94a3b8;font-size:10px" dir="ltr">${escapeHtml(d.system)}</span>` : '';
          return `<li>${named}${note}${system}</li>`;
        })
        .join('')}</ul>`,
    );
  }

  // SOAP, each section on its own block.
  const soap = e.soap || {};
  const soapParts = ['s', 'o', 'a', 'p'].filter((k) => String(soap[k] || '').trim());
  if (soapParts.length) {
    parts.push(heading(t('visitPrint.soap'), p));
    for (const k of soapParts) {
      parts.push(`<div class="vr-block" data-part="soap-${k}" style="margin-bottom:8px"><div style="font-weight:800;font-size:12.5px;color:#334155">${escapeHtml(t(`visitPrint.soapSections.${k}`))}</div><div style="white-space:pre-line;font-size:13px;line-height:1.8">${escapeHtml(soap[k])}</div></div>`);
    }
  }
  if ((e.notes || []).length) {
    parts.push(heading(t('visitPrint.notes'), p));
    parts.push(`<div class="vr-block" style="font-size:12.5px;line-height:1.8">${e.notes.map((n) => `<div>• ${escapeHtml(n.text)}</div>`).join('')}</div>`);
  }

  // The readings taken in the visit.
  if ((e.vitals || []).length) {
    parts.push(heading(t('visitPrint.vitals'), p));
    parts.push(
      `<table class="vr-block" data-part="vitals"><thead><tr><th style="${th}">${escapeHtml(t('visitPrint.reading'))}</th><th style="${th}">${escapeHtml(t('visitPrint.value'))}</th><th style="${th}">${escapeHtml(t('visitPrint.time'))}</th></tr></thead><tbody>${e.vitals
        .map((v) => `<tr><td style="${td}">${escapeHtml(t(`visitPrint.kinds.${v.kind}`))}</td><td style="${td}" dir="ltr">${escapeHtml(v.value)} ${escapeHtml(v.unit || '')}</td><td style="${td}">${escapeHtml(formatDateTime(v.at, { lang }))}</td></tr>`)
        .join('')}</tbody></table>`,
    );
  }

  // The medicines, the labs and the imaging: the visit's prescription and the institution's requests.
  const rx = e.prescription;
  if (rx && (rx.medicines || []).length) {
    parts.push(heading(t('visitPrint.medicines'), p));
    parts.push(
      `<table class="vr-block" data-part="medicines"><thead><tr><th style="${th}">${escapeHtml(t('visitPrint.medicine'))}</th><th style="${th}">${escapeHtml(t('visitPrint.dose'))}</th><th style="${th}">${escapeHtml(t('visitPrint.frequency'))}</th><th style="${th}">${escapeHtml(t('visitPrint.duration'))}</th></tr></thead><tbody>${rx.medicines
        .map((m) => `<tr><td style="${td};font-weight:700">${escapeHtml(m.name)}</td><td style="${td}">${escapeHtml(m.dosage)}</td><td style="${td}">${escapeHtml(m.frequency)}</td><td style="${td}">${escapeHtml(m.duration)}</td></tr>`)
        .join('')}</tbody></table>`,
    );
  }
  const labs = [...((rx && rx.labTests) || []).map((x) => ({ name: x.name, note: x.doctorComment || x.notes })), ...(e.requests || []).filter((r) => r.department === 'lab').map((r) => ({ name: r.title || (r.items || []).map((i) => i.name || i).join('، '), note: r.result && (r.result.text || r.result.summary) }))];
  const imaging = [...((rx && rx.xrays) || []).map((x) => ({ name: x.name, note: x.doctorComment || x.notes })), ...(e.requests || []).filter((r) => r.department === 'radiology').map((r) => ({ name: r.title || (r.items || []).map((i) => i.name || i).join('، '), note: r.result && (r.result.text || r.result.summary) }))];
  for (const [label, list, part] of [[t('visitPrint.labs'), labs, 'labs'], [t('visitPrint.imaging'), imaging, 'imaging']]) {
    if (!list.length) continue;
    parts.push(heading(label, p));
    parts.push(`<ul class="vr-block" data-part="${part}" style="margin:0;padding-inline-start:20px;font-size:13px;line-height:1.8">${list.map((x) => `<li><strong>${escapeHtml(x.name)}</strong>${x.note ? ` — ${escapeHtml(typeof x.note === 'string' ? x.note : JSON.stringify(x.note))}` : ''}</li>`).join('')}</ul>`);
  }

  if ((e.procedures || []).length) {
    parts.push(heading(t('visitPrint.procedures'), p));
    parts.push(`<ul class="vr-block" data-part="procedures" style="margin:0;padding-inline-start:20px;font-size:13px;line-height:1.8">${e.procedures.map((x) => `<li><strong>${escapeHtml(x.name)}</strong>${x.code ? ` <span dir="ltr" style="color:#94a3b8;font-size:11px">${escapeHtml(x.code)}</span>` : ''}${x.notes ? ` — ${escapeHtml(x.notes)}` : ''}</li>`).join('')}</ul>`);
  }

  const referrals = (e.referrals || []).filter((r) => r.status === 'issued');
  const internal = (e.requests || []).filter((r) => !['lab', 'radiology'].includes(r.department));
  if (referrals.length || internal.length) {
    parts.push(heading(t('visitPrint.referrals'), p));
    parts.push(
      `<ul class="vr-block" data-part="referrals" style="margin:0;padding-inline-start:20px;font-size:13px;line-height:1.8">${[
        ...referrals.map((r) => `<li>${escapeHtml(t(`visitPrint.referralKinds.${r.kind}`))}: <strong>${escapeHtml(r.partner && r.partner.name)}</strong>${r.requested ? ` — ${escapeHtml(r.requested)}` : ''}</li>`),
        ...internal.map((r) => `<li>${escapeHtml(t('visitPrint.internal'))}: <strong>${escapeHtml(r.title || r.department)}</strong></li>`),
      ].join('')}</ul>`,
    );
  }

  const certificates = (e.certificates || []).filter((c) => c.status === 'issued');
  if (certificates.length) {
    parts.push(heading(t('visitPrint.certificates'), p));
    parts.push(`<ul class="vr-block" data-part="certificates" style="margin:0;padding-inline-start:20px;font-size:13px;line-height:1.8">${certificates.map((c) => `<li>${escapeHtml(t(`visitPrint.certificateKinds.${c.kind}`))}${c.kind === 'sick_leave' ? ` — ${escapeHtml(t('visitPrint.leaveLine', { days: daysText(c.days, lang), from: formatDate(c.startDate, { lang }), to: formatDate(c.endDate, { lang }) }))}` : ''}</li>`).join('')}</ul>`);
  }

  if ((e.attachments || []).length) {
    parts.push(heading(t('visitPrint.files'), p));
    parts.push(`<ul class="vr-block" data-part="files" style="margin:0;padding-inline-start:20px;font-size:12.5px;line-height:1.8">${e.attachments.map((a) => `<li>${escapeHtml(a.title || a.fileName)}</li>`).join('')}</ul>`);
  }

  if ((e.amendments || []).length) {
    parts.push(heading(t('visitPrint.amendments'), p));
    parts.push(`<ul class="vr-block" data-part="amendments" style="margin:0;padding-inline-start:20px;font-size:12px;line-height:1.8;color:#475569">${e.amendments.map((a) => `<li>${escapeHtml(formatDateTime(a.at, { lang }))} — ${escapeHtml(a.byName || '')}: ${escapeHtml(amendedWhat(a.what, t))} — ${escapeHtml(a.reason)}</li>`).join('')}</ul>`);
  }

  parts.push(signatureBlock({ signature: report.signature, verifyCode: report.draft ? null : e.verifyCode, verifyUrl: report.verifyUrl, qrSvg: report.qrSvg, draft: report.draft, t, lang, primary: p }));

  return page({ head, title: t('visitPrint.reportTitle'), body: parts.join('\n'), t, lang, poweredBy, watermark: report.draft ? draftMark(t) : '' });
}

/* ── the certificate ────────────────────────────────────────────────────── */

/**
 * A medical certificate (M05, M09): a sick leave with its dates, a fitness to
 * work, or a medical report in the doctor's words. The diagnosis prints only
 * when the doctor ticked it.
 * @param {{ print, t, lang, poweredBy }} args — `print` is GET /api/encounters/certificates/:id's
 */
export function buildCertificateHtml({ print, t, lang = 'ar', poweredBy = '' }) {
  if (!print || !print.certificate) throw new Error('buildCertificateHtml: print is required');
  const c = print.certificate;
  const head = letterhead(print.doctor, { lang });
  const p = head.primary;
  const name = c.patientName || (print.patient && print.patient.name) || '';
  const age = ageOf(print.patient && print.patient.dateOfBirth);
  const reason = c.showDiagnosis && c.diagnosisText ? t('visitPrint.cert.because', { diagnosis: c.diagnosisText }) : t('visitPrint.cert.medicalReasons');
  let text;
  if (c.kind === 'sick_leave') {
    text = t('visitPrint.cert.sickLeave', { name, days: daysText(c.days, lang), from: formatDate(c.startDate, { lang }), to: formatDate(c.endDate, { lang }), reason });
  } else if (c.kind === 'fit_to_work') {
    text = t('visitPrint.cert.fitToWork', { name });
  } else {
    text = c.body || '';
  }
  const stamp =
    c.status === 'revoked'
      ? `<div class="vr-block" style="margin:10px 0;padding:8px 12px;border:2px solid #dc2626;color:#dc2626;font-weight:900;text-align:center;border-radius:10px">${escapeHtml(t('visitPrint.cert.revoked'))}</div>`
      : '';
  const body = [
    patientBar([
      [t('visitPrint.patient'), name],
      [t('visitPrint.age'), age !== null ? t('visitPrint.years', { n: age }) : null],
      [t('visitPrint.issued'), formatDate(c.issuedAt || c.createdAt, { lang })],
      [t('visitPrint.institution'), print.institution && print.institution.name],
    ]),
    stamp,
    `<div class="vr-block" data-part="certificate-text" style="font-size:15px;line-height:2.1;white-space:pre-line;margin:14px 0">${escapeHtml(text)}</div>`,
    c.kind !== 'report' && c.body ? `<div class="vr-block" style="font-size:13px;line-height:1.9;color:#334155;white-space:pre-line">${escapeHtml(c.body)}</div>` : '',
    signatureBlock({ signature: { ...print.signature, signedAt: c.issuedAt || c.createdAt }, verifyCode: c.verifyCode, verifyUrl: print.verifyUrl, qrSvg: print.qrSvg, t, lang, primary: p }),
  ].join('\n');
  return page({ head, title: t(`visitPrint.certificateKinds.${c.kind}`), body, t, lang, poweredBy });
}

/* ── the referral ───────────────────────────────────────────────────────── */

/**
 * A referral to a party outside the institution (M08, P06): who it is to, the
 * patient, how urgent, the clinical summary and what is asked.
 * @param {{ print, t, lang, poweredBy }} args — `print` is GET /api/encounters/referrals/:id's
 */
export function buildReferralHtml({ print, t, lang = 'ar', poweredBy = '' }) {
  if (!print || !print.referral) throw new Error('buildReferralHtml: print is required');
  const r = print.referral;
  const head = letterhead(print.doctor, { lang });
  const p = head.primary;
  const partner = r.partner || {};
  const name = r.patientName || (print.patient && print.patient.name) || '';
  const age = ageOf(print.patient && print.patient.dateOfBirth);
  const priorityColour = r.priority === 'emergency' ? '#dc2626' : r.priority === 'urgent' ? '#d97706' : '#0f766e';
  const to = [
    `<div style="font-weight:800;font-size:15px">${escapeHtml(lang === 'en' ? partner.nameEn || partner.name : partner.name)}</div>`,
    partner.specialty ? `<div style="font-size:12.5px;color:#475569">${escapeHtml(partner.specialty)}</div>` : '',
    [partner.address, partner.city].filter(Boolean).length ? `<div style="font-size:12.5px;color:#475569">${escapeHtml([partner.address, partner.city].filter(Boolean).join('، '))}</div>` : '',
    partner.phone ? `<div style="font-size:12.5px;color:#475569" dir="ltr">${escapeHtml(partner.phone)}</div>` : '',
  ].join('');
  const stamp =
    r.status === 'cancelled'
      ? `<div class="vr-block" style="margin:10px 0;padding:8px 12px;border:2px solid #dc2626;color:#dc2626;font-weight:900;text-align:center;border-radius:10px">${escapeHtml(t('visitPrint.ref.cancelled'))}</div>`
      : '';
  const body = [
    `<div class="vr-block" data-part="to" style="display:flex;justify-content:space-between;gap:16px;align-items:flex-start;margin-bottom:14px">
       <div><div style="color:#64748b;font-size:12px">${escapeHtml(t('visitPrint.ref.to'))} — ${escapeHtml(t(`visitPrint.referralKinds.${r.kind}`))}</div>${to}</div>
       <div style="border:2px solid ${priorityColour};color:${priorityColour};border-radius:999px;padding:4px 14px;font-weight:900;font-size:13px">${escapeHtml(t(`visitPrint.priorities.${r.priority || 'routine'}`))}</div>
     </div>`,
    patientBar([
      [t('visitPrint.patient'), name],
      [t('visitPrint.age'), age !== null ? t('visitPrint.years', { n: age }) : null],
      [t('visitPrint.issued'), formatDate(r.issuedAt || r.createdAt, { lang })],
      [t('visitPrint.institution'), print.institution && print.institution.name],
    ]),
    stamp,
    r.summary ? `${heading(t('visitPrint.ref.summary'), p)}<div class="vr-block" data-part="summary" style="white-space:pre-line;font-size:13.5px;line-height:1.9">${escapeHtml(r.summary)}</div>` : '',
    r.requested ? `${heading(t('visitPrint.ref.requested'), p)}<div class="vr-block" data-part="requested" style="white-space:pre-line;font-size:13.5px;line-height:1.9">${escapeHtml(r.requested)}</div>` : '',
    signatureBlock({ signature: { ...print.signature, signedAt: r.issuedAt || r.createdAt }, verifyCode: r.verifyCode, verifyUrl: print.verifyUrl, qrSvg: print.qrSvg, t, lang, primary: p }),
  ].join('\n');
  return page({ head, title: t('visitPrint.ref.title'), body, t, lang, poweredBy });
}
